import { DWCore } from '@windream/dw-core/dwCore';
import { IServiceResponse } from './ajaxHandler/interfaces/iServiceResponse';
import { UserIdentity } from './authentication/userIdentity';
import { IMetadataStore, ObjectType, WindreamAttribute, WindreamAttributeTypes } from './caching';
import {
  AttributeFlags, DateItem, IClonable, IScriptMetadata,
  SCRIPT_SOURCE, Utils, ValueTypes, WindreamEntity, WindreamIdentity
} from './common';
import { ComponentConfig, ComponentInstanceConfig, GlobalConfig, Position, ViewConfig, ViewConfigMetaData } from './config';
import { Style } from './config/models/style';
import { ContextMenu, ContextMenuItem, ContextMenuItemValidator } from './contextMenu';
import { CultureHelper, DateTimeFormatHelper, WindreamFormatter } from './culture';
import { CellData, DataCollection, DataCollectionDisplay, HeaderData, IDataCollectionData, ISortingOption, RowData } from './dataCollections';
import { IResourcePointer, RequestOptions } from './dataProviders';
import {
  IContextMenuExtension, IExtension, IExtensionProvider, IIndexMaskExtension, ILoaderExtension,
  IStorageExtension, ITemplateExtension, IUserConfigExtension, StorageDataModel, ToolbarExtension
} from './extensions';
import { SEARCH_OPERATORS, SEARCH_RELATIONS, SearchAttributeFlags, SearchCondition, SearchGroup, SearchQuery, SearchSorting } from './interface/search';
import { KeyCodes } from './keyboard';
import { ILanguageManager, ILanguageProvider } from './language';
import { OnAfterBind, OnAfterRender, OnBind, OnDestroy, OnExtension, OnInit, OnRefresh, OnSubViewChanging, OnRender, OnSkeleton } from './lifecycle';
import { IComponent, IUiManager } from './loader';
import { Logger } from './logging';
import { MasterList } from './masterList';
import { DxMasterTable, IMasterTableOptions, MasterTable } from './masterTable';
import { CLICKPROGRESSION_MODES, IMasterTreeView, IMasterTreeViewData, IMasterTreeViewItem, IMasterTreeViewOptions2, MasterTreeView, TreeItem, TreeLibNode } from './masterTreeView';
import { DataTypeChecker, IPubSubHandler } from './pubSub';
import {
  ActionResponse, AddHistoryRequestOptions, FetchNodesRequestOptions,
  FileDataSource, GetDetailsRequestOptions,
  IdentityMapper,
  PerformActionRequestOptions, ResultAmountRequestOptions, SearchRequestOptions,
  UpdateDirectoryRightsRequestOptions, UpdateDocumentRightsRequestOptions, UpdateNavigationSettingsRequestOptions,
  UpdateRequestOptions, UploadRequestOptions, WindreamRequestOptions
} from './services';
import { ApplicationSettings, ButtonTypes, FileTransferManagerPopOverHandler, IPopupButtonOptions, IPopupHelper, IPopupOptions, ISkeletonHelper, POPUP_TYPES, SKELETON_TYPES } from './ui';
import { IList } from './ui/components/list';
import { INotificationHelper } from './ui/interfaces';
import { NotificationOptions } from './ui/models/notificationOptions';
import { IWebBridgeHandler, WebBridgeEventTypes } from './webBridge';
import { IWebsocketProvider } from './websocket';

// Export publicly available interfaces
export {
  // Core
  DWCore,
  // Parameters of Lifecycle steps
  IPubSubHandler, IUiManager, IExtensionProvider,
  // Configurations
  ComponentInstanceConfig, ViewConfig, ComponentConfig, GlobalConfig, ViewConfigMetaData, Position,
  // Lifecycle steps
  OnAfterBind, OnBind, OnInit, OnExtension, OnRender, OnSkeleton, OnDestroy, OnAfterRender, OnRefresh, OnSubViewChanging,
  // Models
  DateItem, WindreamEntity, WindreamIdentity, AttributeFlags, ValueTypes,
  // Extensions
  IExtension, IUserConfigExtension, ToolbarExtension, IContextMenuExtension, ILoaderExtension, IIndexMaskExtension, ITemplateExtension, IStorageExtension,
  // Public API related
  NotificationOptions, CultureHelper, IWebsocketProvider, IMetadataStore, IPopupOptions, ButtonTypes, IPopupHelper,
  POPUP_TYPES, DataTypeChecker, Logger, INotificationHelper, ILanguageProvider, ILanguageManager,
  // Other
  IComponent, ActionResponse, UserIdentity, ApplicationSettings, KeyCodes, IClonable,
  // TODO: Remove or re-organize
  Utils, WindreamAttribute, WindreamAttributeTypes, WindreamFormatter, IPopupButtonOptions, Style, StorageDataModel, DateTimeFormatHelper, FileDataSource, ObjectType, IdentityMapper,
  // Search
  SearchAttributeFlags, SearchQuery, SearchSorting, SEARCH_OPERATORS, SEARCH_RELATIONS, SearchCondition, SearchGroup,
  // Master table
  CellData, HeaderData, RowData, DataCollection, DataCollectionDisplay, IMasterTableOptions, ISortingOption, MasterTable, DxMasterTable, IDataCollectionData, MasterList,
  // Master tree
  TreeItem, TreeLibNode, IMasterTreeViewData, MasterTreeView, IMasterTreeView, IMasterTreeViewItem, IMasterTreeViewOptions2 as IMasterTreeViewOptions, CLICKPROGRESSION_MODES,
  // Context Menu
  ContextMenu, ContextMenuItem, ContextMenuItemValidator,
  // Skeletons
  ISkeletonHelper, SKELETON_TYPES,
  // WebBridge
  IWebBridgeHandler, WebBridgeEventTypes,
  // Service request options
  WindreamRequestOptions, GetDetailsRequestOptions,
  UploadRequestOptions, UpdateDocumentRightsRequestOptions, AddHistoryRequestOptions,
  UpdateRequestOptions, UpdateDirectoryRightsRequestOptions, UpdateNavigationSettingsRequestOptions,
  ResultAmountRequestOptions, FetchNodesRequestOptions, PerformActionRequestOptions, SearchRequestOptions,
  // Scripts
  IScriptMetadata, SCRIPT_SOURCE,
  // UI
  IList,
  // File Tramsfer Manager Pop-Over
  FileTransferManagerPopOverHandler
};

export * from './errors';
export * from './interface';
export * from './shared';
export * from './rights';
export * from './ui/components';
export * from './migration';
export * from './common/webService';
export * from './common/lifecycle';
export * from './common/history';


/**
 * The public accessible request executor interface.
 * 
 * @export
 * @interface IPublicRequestExecutor
 */
export interface IPublicRequestExecutor {
  executeRequest(resourcePointer: IResourcePointer, options?: RequestOptions): Promise<IServiceResponse<any>>;
}
